// The due / overdue check reminder widgets: one personal, one per project (IM-027, sprint 7).
//
// ── Why the contract is mirrored here rather than imported ────────────────────────────────────────
// `Sisolco.Dashboards` defines `DashboardWidget<TConfig, TData>` in TypeScript, but the published
// package ships only page entry modules and hashed chunks: there is no static-asset URL from which a
// consumer can import the type. The interface below is therefore a structural mirror — the widget
// satisfies the library's contract by shape, which is all the registry needs. Same reasoning, and the
// same library gap, as `processPortfolioWidget.ts`.
//
// Plain DOM throughout: every value is written with `textContent`, never `innerHTML`. Check titles,
// project names and contact names are ordinary data a user can type, and a formatter that
// interpolated them into markup would be a stored-XSS sink of exactly the kind the library patched in
// its own chart tooltips.

import {
    buildReminderView,
    dueLabel,
    emptyText,
    projectLabel,
    titleLabel,
    toRows,
    urgencyOf,
    type CheckReminderConfig,
    type CheckReminderGroup,
    type CheckReminderRow,
} from "./checkReminderModel";

/** The part of `WidgetRenderContext` this widget uses. Structural mirror; see the note above. */
export interface CheckReminderRenderContext {
    emitClick(intent: unknown, rowScope?: Record<string, unknown>): void;
    cellClickIntent?: unknown;
    localize?(text: string | null | undefined): string;
    editMode?: boolean;
    permissionDenied?: boolean;
}

/** The lifecycle the dashboard host drives. Structural mirror of the library's contract. */
export interface CheckReminderDashboardWidget {
    readonly widgetTypeKey: string;
    render(host: HTMLElement, config: CheckReminderConfig, data: unknown, ctx: CheckReminderRenderContext): void;
    update(config: CheckReminderConfig, data: unknown): void;
    dispose(): void;
}

/** Mirrors `KeyView.Compliance.KeyViewCheckReminderWidgetKeys.MyChecksDue`. */
export const MyChecksDueWidgetKey = "KeyView.MyCheckRecordsDue";

/** Mirrors `KeyView.Compliance.KeyViewCheckReminderWidgetKeys.ProjectChecksDue`. */
export const ProjectChecksDueWidgetKey = "KeyView.ProjectCheckRecordsDue";

/**
 * One renderer, registered under both keys.
 *
 * The two widgets differ in **which rows they are allowed to see**, which is decided server-side by
 * two separate providers under two separate permission keys. They do not differ in how a reminder is
 * drawn, and giving them two renderers would be two places to fix the next wording change.
 */
export class CheckReminderWidget implements CheckReminderDashboardWidget {
    readonly widgetTypeKey: string;

    private host: HTMLElement | null = null;
    private root: HTMLElement | null = null;
    private titleEl: HTMLElement | null = null;
    private bodyEl: HTMLElement | null = null;
    private ctx: CheckReminderRenderContext | null = null;

    constructor(widgetTypeKey: string = MyChecksDueWidgetKey) {
        this.widgetTypeKey = widgetTypeKey;
    }

    render(host: HTMLElement, config: CheckReminderConfig, data: unknown, ctx: CheckReminderRenderContext): void {
        this.host = host;
        this.ctx = ctx;
        host.innerHTML = "";

        const root = document.createElement("div");
        root.className = "keyview-check-reminders";

        const titleEl = document.createElement("div");
        titleEl.className = "title";

        const bodyEl = document.createElement("div");
        bodyEl.className = "reminder-body";

        root.appendChild(titleEl);
        root.appendChild(bodyEl);
        host.appendChild(root);

        this.root = root;
        this.titleEl = titleEl;
        this.bodyEl = bodyEl;

        this.draw(config, data);
    }

    update(config: CheckReminderConfig, data: unknown): void {
        if (!this.root) return;
        this.draw(config, data);
    }

    dispose(): void {
        if (this.host) this.host.innerHTML = "";
        this.host = null;
        this.root = null;
        this.titleEl = null;
        this.bodyEl = null;
        this.ctx = null;
    }

    private text(value: string | null | undefined): string {
        const localize = this.ctx?.localize;
        return localize ? localize(value) : (value ?? "");
    }

    private draw(config: CheckReminderConfig, data: unknown): void {
        const body = this.bodyEl;
        if (!body) return;

        if (this.titleEl)
            this.titleEl.textContent = this.text(config?.Title ?? "Checks due");

        body.innerHTML = "";

        const view = buildReminderView(toRows(data), config);

        if (view.total === 0) {
            const empty = document.createElement("div");
            empty.className = "reminder-empty";
            empty.textContent = this.text(emptyText(config));
            body.appendChild(empty);
            return;
        }

        for (const group of view.groups)
            body.appendChild(this.drawGroup(group, config));

        const summary = document.createElement("div");
        summary.className = "reminder-summary";
        summary.textContent = view.overdue > 0
            ? `${view.total} outstanding · ${view.overdue} overdue`
            : `${view.total} outstanding`;
        body.appendChild(summary);
    }

    private drawGroup(group: CheckReminderGroup, config: CheckReminderConfig): HTMLElement {
        const section = document.createElement("div");
        section.className = `reminder-group reminder-group-${group.key.toLowerCase()}`;
        section.dataset.groupKey = group.key;

        // An empty label is the ungrouped case; drawing a blank heading would just eat a line.
        if (group.label) {
            const heading = document.createElement("div");
            heading.className = "reminder-group-heading";
            heading.textContent = `${this.text(group.label)} (${group.rows.length})`;
            section.appendChild(heading);
        }

        const list = document.createElement("ul");
        list.className = "reminder-list";

        for (const row of group.rows)
            list.appendChild(this.drawRow(row, config));

        section.appendChild(list);
        return section;
    }

    private drawRow(row: CheckReminderRow, config: CheckReminderConfig): HTMLElement {
        const urgency = urgencyOf(row);

        const li = document.createElement("li");
        li.className = `reminder-item reminder-item-${urgency.toLowerCase()}`;
        li.dataset.recordId = String(row.RecordId);
        li.dataset.urgency = urgency;

        const title = document.createElement("span");
        title.className = "reminder-title";
        title.textContent = this.text(titleLabel(row));
        // The title ellipsises rather than wrapping, so the full text has to stay reachable. Set from
        // the SAME string and as plain text, not markup, so it carries no more risk than the
        // textContent above.
        title.title = title.textContent;
        li.appendChild(title);

        const meta = document.createElement("span");
        meta.className = "reminder-meta";

        // ⚠️ The project line is drawn for a record with NO project too — `projectLabel` words that
        // case rather than returning a blank. See the note there for why a blank is the wrong answer.
        if (config?.ShowProject !== false) {
            const project = document.createElement("span");
            project.className = row.ProjectId === null || row.ProjectId === undefined
                ? "reminder-project reminder-project-none"
                : "reminder-project";
            project.textContent = this.text(projectLabel(row));
            meta.appendChild(project);
        }

        if (config?.ShowResponsible !== false && row.ResponsibleContactName) {
            const responsible = document.createElement("span");
            responsible.className = "reminder-responsible";
            responsible.textContent = this.text(row.ResponsibleContactName);
            meta.appendChild(responsible);
        }

        li.appendChild(meta);

        const due = document.createElement("span");
        due.className = "reminder-due";
        due.textContent = this.text(dueLabel(row));
        li.appendChild(due);

        // The drill-through. Only an authored cell-level intent is honoured: this widget ships no
        // default target, because nothing is drawn as clickable unless there is somewhere to go — a
        // control that does nothing is worse than none.
        const intent = this.ctx?.cellClickIntent;

        if (intent && !this.ctx?.editMode && !this.ctx?.permissionDenied) {
            li.classList.add("is-clickable");
            li.style.cursor = "pointer";
            li.addEventListener("click", () => {
                this.ctx?.emitClick(intent, {
                    RecordId: row.RecordId,
                    ProjectId: row.ProjectId ?? null,
                    ResponsibleContactId: row.ResponsibleContactId ?? null,
                    Urgency: urgency,
                    DaysUntilDue: row.DaysUntilDue ?? null,
                });
            });
        }

        return li;
    }
}
